import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import cx from "classnames";
import { forwardRef, } from "react";
import cn from "variant-classnames";
import { toolkitCx } from "../../../constants";
import ConditionalWrapper from "../ConditionalWrapper";
import Image from "../Image";
import Text from "../Text";
const variants = (fill, hoverable, isActive) => ({
    $all: cx("rounded-full flex items-center justify-center min-w-min select-none", { "border-transparent border": fill }, toolkitCx.layout.transition.colors),
    variant: {
        gradient: "border-transparent border",
        secondary: cx(toolkitCx.layout.text.secondary, {
            [`${toolkitCx.layout.border.secondary} ${toolkitCx.layout.border.weight.default}`]: !fill,
            "bg-secondary/20": fill || (!fill && isActive),
            "hover:bg-secondary/20": !fill && hoverable,
            "bg-secondary/30": isActive && fill,
            "hover:bg-secondary/30": hoverable && fill,
        }),
        silent: cx(toolkitCx.layout.text.default, {
            [`border-black/50 dark:border-gray-200/50 ${toolkitCx.layout.border.weight.default}`]: !fill,
            [toolkitCx.layout.background.separator]: (fill && !isActive) || (!fill && isActive),
            "hover:bg-gray-200/40 dark:hover:bg-gray-800": !fill && hoverable,
            "bg-gray-200/50 dark:bg-gray-700": isActive && fill,
            "hover:bg-gray-200/90 dark:hover:bg-gray-700": hoverable && fill,
        }),
        muted: cx({
            [`${toolkitCx.layout.border.weight.default} ${toolkitCx.layout.border.muted}`]: !fill,
            "bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-gray": (fill && !isActive) || (!fill && isActive),
            "bg-primary/20 dark:bg-primary/30": isActive && fill,
            "hover:bg-gray-400 hover:dark:bg-gray-600/75": hoverable,
        }),
        success: cx(toolkitCx.layout.text.success, {
            [`${toolkitCx.layout.border.weight.default} ${toolkitCx.layout.border.success} `]: !fill,
            "bg-success/20": (fill && !isActive) || (!fill && isActive),
            "hover:bg-success/20": !fill && hoverable,
            "bg-success/30": isActive && fill,
            "hover:bg-success/30": hoverable && fill,
        }),
        warning: cx(toolkitCx.layout.text.success, {
            [`${toolkitCx.layout.border.weight.default} ${toolkitCx.layout.border.warning} `]: !fill,
            "bg-warning/20": (fill && !isActive) || (!fill && isActive),
            "hover:bg-warning/20": !fill && hoverable,
            "bg-warning/30": isActive && fill,
            "hover:bg-warning/30": hoverable && fill,
        }),
        primary: cx(toolkitCx.layout.text.primary, {
            [`${toolkitCx.layout.border.weight.default} ${toolkitCx.layout.border.primary} `]: !fill,
            "bg-primary/20": (fill && !isActive) || (!fill && isActive),
            "hover:bg-primary/20": !fill && hoverable,
            "bg-primary/30": isActive && fill,
            "hover:bg-primary/30": hoverable && fill,
        }),
        info: cx(toolkitCx.layout.text.primary, {
            [`${toolkitCx.layout.border.weight.default} ${toolkitCx.layout.border.primary} `]: !fill,
            "bg-primary/20": (fill && !isActive) || (!fill && isActive),
            "hover:bg-primary/20": !fill && hoverable,
            "bg-primary/30": isActive && fill,
            "hover:bg-primary/30": hoverable && fill,
        }),
        error: cx(toolkitCx.layout.text.error, {
            [`${toolkitCx.layout.border.weight.default} ${toolkitCx.layout.border.error} `]: !fill,
            "bg-error/20": (fill && !isActive) || (!fill && isActive),
            "hover:bg-error/20": !fill && hoverable,
            "bg-error/30": isActive && fill,
            "hover:bg-error/30": hoverable && fill,
        }),
    },
});
const imageVariants = {
    $all: "!rounded-full",
    size: {
        "2xs": "h-4",
        xs: "h-5",
        sm: "h-6",
        md: "h-7",
        lg: "h-8",
    },
};
const textVariants = {
    $all: "flex items-center justify-center",
    size: {
        "2xs": cx("h-4", toolkitCx.layout.spacing.horizontal.sm),
        xs: cx("h-5", toolkitCx.layout.spacing.horizontal.sm),
        sm: cx("h-6", toolkitCx.layout.spacing.horizontal.md),
        md: cx("h-7", toolkitCx.layout.spacing.horizontal.md),
        lg: cx("h-8", toolkitCx.layout.spacing.horizontal.md),
    },
};
const TEXT_SIZE_MAP = {
    "2xs": "2xs",
    xs: "2xs",
    sm: "xs",
    md: "sm",
    lg: "base",
};
const TEXT_VARIANT_MAP = {
    error: "error",
    gradient: "normal",
    info: "info",
    primary: "info",
    secondary: "secondary",
    silent: "silent",
    muted: "muted",
    success: "success",
    warning: "warning",
};
/**
 * Renders a badge component with a background color and text.
 *
 * @param {IBadgeProps} props - Component props
 * @param {boolean} [props.fill=true] - If true, the badge has a background color, otherwise it's transparent
 * @param {boolean} [props.hoverable=false] - If true, the badge renders a hover state.
 * @param {boolean} [props.isActive=false] - If true, the badge renders an active state.
 * @param {string} props.size - The size of the badge. Must be one of "xs", "sm", "base" or "lg"
 * @param {string} [props.variant="gradient"] - The variant of the badge. Must be one of "error", "gradient", "info", "primary", "secondary", "silent", "success" or "warning"
 * @param {string} [props.className] - Additional CSS classes to apply to the component
 * @param {React.ReactNode} [props.children] - Text or other elements to be rendered inside the badge
 * @param {React.Ref<HTMLDivElement>} [forwardedRef] - Reference to the underlying HTMLDivElement
 *
 * @returns {JSX.Element} - The rendered badge component
 */
const Badge = ({ className, children, fill = true, imageSrc, hoverable = false, isActive = false, size, variant = "gradient", ...rest }, forwardedRef) => {
    const badgeClasses = cn(variants(fill, hoverable, isActive), {
        className,
        variant,
    });
    const textClasses = cn(textVariants, { size });
    const imageClasses = cn(imageVariants, { size });
    return (_jsx("div", { className: badgeClasses, ref: forwardedRef, ...rest, children: _jsxs(ConditionalWrapper, { condition: variant === "gradient", wrapper: (children) => (_jsx("div", { className: cx("rounded-full p-[1px]", {
                    "bg-gradient-to-r from-secondary to-primary": !fill,
                }), children: _jsx("div", { className: cx("rounded-full", toolkitCx.layout.background.default, {
                        "bg-gradient-to-r from-secondary/20 to-primary/20": fill,
                        "flex items-center": !!imageSrc,
                    }), children: children }) })), children: [_jsx(Text, { className: textClasses, gradient: variant === "gradient", tag: "span", size: TEXT_SIZE_MAP[size], style: {
                        fontSize: ["xs", "2xs"].includes(size) ? 10 : undefined, // Enforce font size for 2xs and xs to 10px so the text doesn't get too big for the overall size of the badge.
                    }, variant: TEXT_VARIANT_MAP[variant], children: children }), imageSrc && (_jsx("div", { className: "-ml-1 flex flex-shrink-0 items-center", children: typeof imageSrc === "string" ? (_jsx(Image, { className: imageClasses, src: imageSrc })) : (_jsxs(_Fragment, { children: [_jsx(Image, { className: imageClasses, src: imageSrc[0] }), _jsx(Image, { className: cx("z-10", {
                                    "-ml-1": size === "2xs",
                                    "-ml-2": ["xs", "sm"].includes(size),
                                    "-ml-3": ["md", "lg"].includes(size),
                                }, imageClasses), src: imageSrc[1] })] })) }))] }) }));
};
export default forwardRef(Badge);
//# sourceMappingURL=Badge.js.map